import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { getCountryCallingCode } from 'react-phone-number-input'
import type { Country } from 'react-phone-number-input'

import styles from './CountrySelect.module.scss'

// Prop shape that react-phone-number-input passes to a `countrySelectComponent`.
// See node_modules/react-phone-number-input/modules/PhoneInputWithCountry.js:427
// for the authoritative invocation site.
type Option = {
  value: Country | undefined
  label: string
  divider?: boolean
}

export type CountrySelectProps = {
  'name'?: string
  'value'?: Country
  'options': Option[]
  'onChange': (country?: Country) => void
  'onFocus'?: (event: React.FocusEvent<HTMLElement>) => void
  'onBlur'?: (event: React.FocusEvent<HTMLElement>) => void
  'disabled'?: boolean
  'readOnly'?: boolean
  'aria-label'?: string
}

/**
 * Styled country picker used as `countrySelectComponent` on
 * react-phone-number-input. Renders a `+{dialCode} ▼` trigger and a combobox
 * panel (search input + filtered listbox) — matches the production
 * intl-tel-input UX the native `<select>` default can't reproduce, including
 * multi-character search ("new" → "New Zealand" + "New Caledonia").
 */
const CountrySelect: React.FC<CountrySelectProps> = ({
  name,
  value,
  options,
  onChange,
  onFocus,
  onBlur,
  disabled,
  readOnly,
  'aria-label': ariaLabel = 'Country',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const activeItemRef = useRef<HTMLLIElement>(null)

  // Only the countries themselves are selectable — drop any divider entries
  // or the international "ZZ" pseudo-option that RPNI can add.
  const selectable = useMemo(
    () =>
      options.filter((opt) => opt.value && !opt.divider) as Array<
        Option & { value: Country }
      >,
    [options],
  )

  // Filter: case-insensitive substring match on country name and dial code.
  // Users can search by name ("new" → New Zealand, New Caledonia) or by
  // dial code ("44" → United Kingdom, etc.).
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return selectable
    return selectable.filter((opt) => {
      if (opt.label.toLowerCase().includes(needle)) return true
      const dialCode = getCountryCallingCode(opt.value)
      return dialCode.includes(needle) || `+${dialCode}`.includes(needle)
    })
  }, [selectable, query])

  // Keep the active index valid as the filtered set shrinks/grows.
  useEffect(() => {
    if (!isOpen) return
    if (filtered.length === 0) {
      setActiveIndex(-1)
      return
    }
    setActiveIndex((prev) => (prev < 0 || prev >= filtered.length ? 0 : prev))
  }, [filtered, isOpen])

  const triggerDialCode = value ? getCountryCallingCode(value) : ''

  const open = useCallback(() => {
    if (disabled || readOnly) return
    setIsOpen(true)
    setQuery('')
    const currentIndex = selectable.findIndex((opt) => opt.value === value)
    setActiveIndex(currentIndex >= 0 ? currentIndex : 0)
  }, [disabled, readOnly, selectable, value])

  const close = useCallback((closeOptions: { returnFocus?: boolean } = {}) => {
    setIsOpen(false)
    setActiveIndex(-1)
    setQuery('')
    if (closeOptions.returnFocus) {
      triggerRef.current?.focus()
    }
  }, [])

  const selectAt = useCallback(
    (index: number) => {
      const next = filtered[index]
      if (!next) return
      onChange(next.value)
      close({ returnFocus: true })
    },
    [close, onChange, filtered],
  )

  // Click-outside closes the panel. Attach only while open to avoid always-on
  // document listeners.
  useEffect(() => {
    if (!isOpen) return
    const handler = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, close])

  // Keep the focused row in view when navigating via keyboard. `scrollIntoView`
  // is unimplemented in jsdom, so guard for tests.
  useEffect(() => {
    if (isOpen && activeItemRef.current?.scrollIntoView) {
      activeItemRef.current.scrollIntoView({ block: 'nearest' })
    }
  }, [isOpen, activeIndex])

  // Auto-focus the search input when the panel opens.
  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus()
    }
  }, [isOpen])

  const onTriggerKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    if (disabled || readOnly) return
    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault()
        open()
        break
    }
  }

  const onSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
        break
      case 'Home':
        event.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        event.preventDefault()
        setActiveIndex(filtered.length - 1)
        break
      case 'Enter':
        event.preventDefault()
        if (activeIndex >= 0) selectAt(activeIndex)
        break
      case 'Escape':
        event.preventDefault()
        close({ returnFocus: true })
        break
      case 'Tab':
        close()
        break
    }
  }

  const listboxId = name ? `${name}-listbox` : undefined
  const searchId = name ? `${name}-search` : undefined
  const activeDescendantId =
    isOpen && activeIndex >= 0 && listboxId
      ? `${listboxId}-opt-${filtered[activeIndex]?.value}`
      : undefined

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className={styles.trigger}
        disabled={disabled || readOnly}
        name={name}
        onBlur={onBlur}
        onClick={() => (isOpen ? close() : open())}
        onFocus={onFocus}
        onKeyDown={onTriggerKeyDown}
        ref={triggerRef}
        type="button"
      >
        <span className={styles.triggerDialCode}>
          {triggerDialCode ? `+${triggerDialCode}` : ''}
        </span>
        <span aria-hidden="true" className={styles.triggerArrow}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className={styles.panel}>
          <input
            aria-activedescendant={activeDescendantId}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-expanded
            aria-label="Search countries"
            autoComplete="off"
            className={styles.searchInput}
            id={searchId}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search countries"
            ref={searchRef}
            role="combobox"
            type="text"
            value={query}
          />
          {filtered.length === 0 ? (
            <div className={styles.emptyMessage} role="status">
              No countries match &ldquo;{query}&rdquo;
            </div>
          ) : (
            <ul
              className={styles.listbox}
              id={listboxId}
              role="listbox"
              tabIndex={-1}
            >
              {filtered.map((option, index) => {
                const dialCode = getCountryCallingCode(option.value)
                const isSelected = option.value === value
                const isActive = index === activeIndex
                return (
                  <li
                    aria-selected={isSelected}
                    className={classNames(styles.option, {
                      [styles.optionActive]: isActive,
                      [styles.optionSelected]: isSelected,
                    })}
                    id={
                      listboxId ? `${listboxId}-opt-${option.value}` : undefined
                    }
                    key={option.value}
                    onClick={() => selectAt(index)}
                    onMouseEnter={() => setActiveIndex(index)}
                    ref={isActive ? activeItemRef : null}
                    role="option"
                  >
                    <span className={styles.optionLabel}>{option.label}</span>
                    <span
                      className={styles.optionDialCode}
                    >{`+${dialCode}`}</span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default CountrySelect
